import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { datasourcesService } from '@/services/datasources';
import { RootState } from '@/stores/redux/store';

// Track in-flight requests to prevent duplicate calls
const inFlightRequests: { [key: string]: Promise<any> } = {};

export const fetchDatasources = createAsyncThunk(
    '/fetchDatasources',
    async (payload, { dispatch, getState }) => {
        const state = getState() as RootState
        const workspaceId = state.activeWorkspace?.id;
        if (!workspaceId || workspaceId === 'undefined' || workspaceId === 'null' || workspaceId.trim() === '') {
            console.warn('fetchDatasources called without valid active workspace:', workspaceId);
            return [];
        }

        // Check if there's already an in-flight request for this workspace
        const requestKey = `datasources_${workspaceId}`;
        if (inFlightRequests[requestKey]) {
            return inFlightRequests[requestKey];
        }

        // Create and store the promise
        const requestPromise = datasourcesService.fetchDatasources(workspaceId)
            .finally(() => {
                delete inFlightRequests[requestKey];
            });

        inFlightRequests[requestKey] = requestPromise;
        return requestPromise;
    }
)

const datasourcesSlice = createSlice({
    name: 'datasources',
    initialState: {
        datasources: [] as any[],
        status: 'idle',
        error: null as string | null
    },
    reducers: {
        setDatasources: (state, action: PayloadAction<any>) => {
            state.datasources = action.payload
        },
        updateDatasource: (state, action: PayloadAction<any>) => {
            if (!action.payload?.id) {
                console.warn('⚠️ Redux: updateDatasource called without valid id', action.payload);
                return;
            }
            const index = state.datasources.findIndex((datasource: any) => datasource?.id === action.payload.id)
            if (index !== -1) {
                const oldDatasource = state.datasources[index];
                const oldStatus = oldDatasource?.status;
                const newStatus = action.payload.status;

                console.log('🔄 Redux: Updating datasource', {
                    id: action.payload.id,
                    oldStatus,
                    newStatus,
                    statusChanged: oldStatus !== newStatus
                });

                state.datasources = state.datasources.map((datasource: any) =>
                    datasource?.id === action.payload.id ? { ...datasource, ...action.payload } : datasource
                )
            } else {
                console.log('➕ Redux: Adding new datasource', action.payload);
                state.datasources.push({ ...action.payload })
            }
        },
        addDatasource: (state, action: PayloadAction<any>) => {
            state.datasources.push(action.payload)
        },
        removeDatasource: (state, action: PayloadAction<string>) => {
            const idToRemove = action.payload;
            const beforeCount = state.datasources.length;
            state.datasources = state.datasources.filter((datasource: any) => datasource.id !== idToRemove);
            const afterCount = state.datasources.length;
            console.log('🗑️ Redux: removeDatasource', { idToRemove, beforeCount, afterCount, removed: beforeCount - afterCount });
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchDatasources.pending, (state, action) => {
            state.status = 'loading'
        })
        builder.addCase(fetchDatasources.fulfilled, (state, action) => {
            state.status = 'succeeded'
            state.datasources = action.payload
        })
        builder.addCase(fetchDatasources.rejected, (state, action) => {
            state.status = 'failed'
            state.error = action.error.message || null
        })
    }
})

export const { setDatasources, addDatasource, removeDatasource, updateDatasource } = datasourcesSlice.actions
export default datasourcesSlice.reducer